import { useState, useCallback, useRef, useEffect } from 'react';
import langFamilyOrder from '../data/lang-family-order.json';

const API_BASE =
  import.meta.env.VITE_API_BASE_URL ??
  'https://ojbr4cjpvh.execute-api.ap-northeast-1.amazonaws.com';

const HISTORY_KEY = 'lexicon-search-history';
const HISTORY_MAX = 100;

function loadHistory(): string[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    return raw ? (JSON.parse(raw) as string[]).slice(0, HISTORY_MAX) : [];
  } catch { return []; }
}

function saveHistory(terms: string[]): void {
  try { localStorage.setItem(HISTORY_KEY, JSON.stringify(terms.slice(0, HISTORY_MAX))); }
  catch { /* quota exceeded */ }
}

function addToHistory(term: string): string[] {
  const trimmed = term.trim();
  if (!trimmed) return loadHistory();
  const prev = loadHistory().filter(t => t !== trimmed);
  const next = [trimmed, ...prev].slice(0, HISTORY_MAX);
  saveHistory(next);
  return next;
}

const FAMILY_ORDER: Record<string, number> = langFamilyOrder as Record<string, number>;

interface Sound {
  ipa?: string;
  audio?: string;
  tags?: string[];
  zh_pron?: string;
  roman?: string;
  other?: string;
  hangeul?: string;
}

interface Sense {
  glosses?: string[];
  raw_glosses?: string[];
  tags?: string[];
  categories?: string[];
  examples?: { text?: string; english?: string; translation?: string }[];
}

interface Form {
  form?: string;
  tags?: string[];
  ipa?: string;
  roman?: string;
  source?: string;
}

interface Translation {
  lang?: string;
  code?: string;
  word?: string;
  sense?: string;
  roman?: string;
  alt?: string;
  tags?: string[];
}

interface Compound {
  source_dict?: string;
  entry_id?: string | null;
  volume?: number | null;
  page?: number | null;
  char_count?: number;
  char_pronunciations?: string[] | null;
  headword_xhzd_id?: string;
  headword_char?: string | null;
  headword_position?: string;
  headword_index?: number | null;
  gloss_zh?: string | null;
  gloss_en?: string | null;
}

interface ParentEntry {
  word: string;
  pos: string;
  senses?: Sense[];
  definitions?: Record<string, string>;
  transcriptions?: Record<string, string>;
  etymology?: string;
  compounds?: Compound[] | null;
  tangut_meta?: Record<string, string | number> | null;
}

interface FormOf {
  lemma_ref: string;
  headword?: string;
  form_labels?: string[];
}

interface LexiconEntry {
  word: string;
  target_language: string;
  lang_name: string;
  wikt_lang_code: string;
  pos: string;
  has_ipa: boolean;
  has_audio: boolean;
  has_forms: boolean;
  sounds_count: number;
  forms_count: number;
  senses_count: number;
  translations_count: number;
  sounds: Sound[];
  senses: Sense[];
  forms: Form[];
  translations: Translation[];
  etymology_text: string;
  synonyms?: { word: string; tags?: string[]; raw_tags?: string[]; sense?: string }[];
  dialect_synonyms?: Record<string, string[]> | null;
  definitions?: Record<string, string>;
  source?: string;
  form_of?: FormOf | null;
  parent_entry?: ParentEntry | null;
  glyph_name?: string;
  unicode_codepoint?: string | null;
  reading_values?: string[];
  labat_number?: string | null;
  meszl_number?: string | null;
  semantic_category?: string | null;
  compounds?: Compound[] | null;
  tangut_meta?: Record<string, string | number> | null;
}

interface SearchResponse {
  word: string;
  edition: string;
  count: number;
  items: LexiconEntry[];
}

const POS_COLORS: Record<string, string> = {
  noun: 'bg-blue-500/20 text-blue-300',
  verb: 'bg-green-500/20 text-green-300',
  adj: 'bg-yellow-500/20 text-yellow-300',
  adv: 'bg-purple-500/20 text-purple-300',
  pron: 'bg-pink-500/20 text-pink-300',
  prep: 'bg-orange-500/20 text-orange-300',
  conj: 'bg-red-500/20 text-red-300',
  intj: 'bg-teal-500/20 text-teal-300',
  name: 'bg-indigo-500/20 text-indigo-300',
};

function PosTag({ pos }: { pos: string }) {
  const color = POS_COLORS[pos] || 'bg-zinc-500/20 text-zinc-300';
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${color}`}>
      {pos}
    </span>
  );
}

function IpaDisplay({ sounds }: { sounds: Sound[] }) {
  const ipas = sounds.filter((s) => s.ipa);
  if (ipas.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-1">
      {ipas.map((s, i) => (
        <span key={i} className="text-accent-teal font-mono text-sm">
          /{s.ipa}/
          {s.tags && s.tags.length > 0 && (
            <span className="text-zinc-500 text-xs ml-1">
              ({s.tags.join(', ')})
            </span>
          )}
        </span>
      ))}
    </div>
  );
}

const ROMANIZATION_SYSTEMS: Record<string, string> = {
  Pinyin: 'Pinyin',
  Bopomofo: 'Zhuyin',
  Jyutping: 'Jyutping',
  Yale: 'Yale',
  'Phak-fa-su': 'PFS',
  'Hakka-Romanization-System': 'HRS',
  POJ: 'POJ',
  'Tâi-lô': 'Tâi-lô',
  'Foochow-Romanized': 'BUC',
  'Kienning-Colloquial-Romanized': 'KCR',
  "Pouseng-Ping'ing": "Pouseng",
};

function RomanizationDisplay({ sounds }: { sounds: Sound[] }) {
  const romans = sounds.filter((s) => s.zh_pron || s.roman || s.other || s.hangeul);
  if (romans.length === 0) return null;

  const groups: Record<string, { system: string; value: string; detail: string }[]> = {};
  for (const s of romans) {
    const val = s.zh_pron || s.roman || s.other || s.hangeul || '';
    const tags = s.tags || [];
    const dialect = tags[0] || 'Other';
    const systemTag = tags.find((t) => t in ROMANIZATION_SYSTEMS);
    const system = systemTag ? (ROMANIZATION_SYSTEMS[systemTag] ?? systemTag) : tags.slice(1).join(' · ') || '';
    const detail = tags.filter((t) => t !== dialect && !(t in ROMANIZATION_SYSTEMS) && t !== 'Standard' && t !== 'Wiktionary-specific').join(' · ');
    if (!groups[dialect]) groups[dialect] = [];
    const key = `${val}|${system}`;
    if (!groups[dialect].some((g) => `${g.value}|${g.system}` === key)) {
      groups[dialect].push({ system, value: val, detail });
    }
  }

  return (
    <div className="mt-2 space-y-1">
      {Object.entries(groups).map(([dialect, items]) => (
        <div key={dialect} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="text-xs text-zinc-500 w-16 sm:w-20 shrink-0">{dialect}</span>
          {items.slice(0, 6).map((r, i) => (
            <span key={i} className="text-amber-300/90 font-mono text-sm">
              {r.value}
              {r.system && (
                <span className="text-zinc-500 text-xs ml-1 font-sans">
                  {r.system}
                </span>
              )}
            </span>
          ))}
          {items.length > 6 && (
            <span className="text-zinc-600 text-xs">+{items.length - 6}</span>
          )}
        </div>
      ))}
    </div>
  );
}

const DIALECT_ORDER = [
  'Classical-Chinese', 'Written-vernacular-Chinese', 'formal',
  'Mandarin', 'Northeastern-Mandarin', 'Jilu-Mandarin', 'Zhongyuan-Mandarin',
  'Jianghuai-Mandarin', 'Southwestern-Mandarin', 'Lanyin-Mandarin',
  'Cantonese', 'Hakka', 'Jin', 'Min-Dong', 'Min-Nan', 'Hokkien', 'Teochew',
  'Min-Bei', 'Gan', 'Wu', 'Xiang', 'Pinghua', 'Dungan',
];

function DialectSynonyms({ entry, onSearchForm }: {
  entry: LexiconEntry;
  onSearchForm?: (f: string) => void;
}) {
  const synonyms = entry.synonyms;
  const dialectSyns = entry.dialect_synonyms;

  if ((!synonyms || synonyms.length < 5) && !dialectSyns) return null;

  const groups: Record<string, { word: string; region?: string }[]> = {};

  if (synonyms && synonyms.length >= 5) {
    for (const s of synonyms) {
      const tags = [...(s.tags || []), ...(s.raw_tags || [])];
      const dialect = tags[0] || 'general';
      const region = tags.slice(1).join(' · ');
      if (!groups[dialect]) groups[dialect] = [];
      if (!groups[dialect].some(g => g.word === s.word)) {
        groups[dialect].push({ word: s.word, region });
      }
    }
  } else if (dialectSyns) {
    for (const [key, words] of Object.entries(dialectSyns)) {
      const parts = key.split('·');
      const dialect = parts[0] || 'general';
      const region = parts.slice(1).join(' · ');
      if (!groups[dialect]) groups[dialect] = [];
      for (const w of words) {
        if (!groups[dialect].some(g => g.word === w)) {
          groups[dialect].push({ word: w, region });
        }
      }
    }
  }

  if (Object.keys(groups).length === 0) return null;

  const sorted = Object.entries(groups).sort(([a], [b]) => {
    const ia = DIALECT_ORDER.indexOf(a);
    const ib = DIALECT_ORDER.indexOf(b);
    return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
  });

  return (
    <div className="mt-3">
      <h4 className="text-xs font-medium text-zinc-400 mb-1.5 uppercase tracking-wider">
        Dialect Synonyms ({Object.values(groups).reduce((n, g) => n + g.length, 0)})
      </h4>
      <div className="space-y-1">
        {sorted.map(([dialect, items]) => (
          <div key={dialect} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span className="text-xs text-zinc-500 w-24 sm:w-32 shrink-0 truncate" title={dialect}>
              {dialect}
            </span>
            {items.slice(0, 8).map((item, i) => (
              <button
                key={i}
                className="text-emerald-400/80 text-sm hover:text-emerald-300 hover:underline transition-colors"
                onClick={() => onSearchForm?.(item.word)}
                title={item.region || dialect}
              >
                {item.word}
              </button>
            ))}
            {items.length > 8 && (
              <span className="text-zinc-600 text-xs">+{items.length - 8}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const TENSE_ORDER = ['infinitive','present','past','future','conditional','imperative','subjunctive','imperfect','pluperfect','aorist','perfect','gerund','participle','transgressive','verbal-noun'];
const CASE_ORDER = ['nominative','genitive','dative','accusative','instrumental','locative','vocative','prepositional','ablative','partitive','illative','inessive','elative','allative','adessive','abessive','essive','translative','comitative'];
const BASIC_TENSES = new Set(['infinitive','present','past','future','imperative']);
const ADVANCED_TENSES = new Set(['conditional','subjunctive','imperfect','pluperfect','aorist','perfect','gerund','participle','transgressive','verbal-noun']);

function isValidForm(f: Form) {
  return f.form && f.form !== 'no-table-tags' && !f.tags?.includes('table-tags') && !f.tags?.includes('inflection-template');
}

function groupBy(items: Form[], orderList: string[]) {
  const groups: Record<string, Form[]> = {};
  for (const f of items) {
    const key = f.tags?.find(t => orderList.includes(t)) || 'other';
    if (!groups[key]) groups[key] = [];
    groups[key].push(f);
  }
  return Object.entries(groups).sort(([a], [b]) => {
    const ia = orderList.indexOf(a) === -1 ? 99 : orderList.indexOf(a);
    const ib = orderList.indexOf(b) === -1 ? 99 : orderList.indexOf(b);
    return ia - ib;
  });
}

function TenseGroup({ label, items, groupKey, onSearchForm }: {
  label: string; items: Form[]; groupKey: string; onSearchForm?: (f: string) => void;
}) {
  return (
    <div className="mb-3">
      <h5 className="text-xs font-medium text-accent-teal/80 uppercase tracking-wider mb-1">{label}</h5>
      <div className="overflow-x-auto">
        <table className="text-xs w-full min-w-[300px]">
          <tbody>
            {items.map((f, i) => (
              <tr key={`${groupKey}-${i}`} className="border-b border-zinc-800/50 hover:bg-zinc-800/40">
                <td className="py-1 pr-3">
                  <button
                    className="text-gray-200 hover:text-accent-teal transition-colors font-mono"
                    onClick={() => onSearchForm?.(f.form || '')}
                  >
                    {f.form}
                  </button>
                </td>
                <td className="py-1 text-zinc-500">
                  {f.tags?.filter(t => t !== label.toLowerCase()).join(', ')}
                </td>
                {f.roman && <td className="py-1 text-amber-300/70 font-mono pl-2">{f.roman}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ParadigmTable({ forms, onSearchForm }: { forms: Form[]; onSearchForm?: (f: string) => void }) {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const conj = forms.filter(f => f.source === 'conjugation' && isValidForm(f));
  const decl = forms.filter(f => f.source === 'declension' && isValidForm(f));
  const other = forms.filter(f => !f.source && isValidForm(f));

  const conjGroups = groupBy(conj, TENSE_ORDER);
  const basicGroups = conjGroups.filter(([key]) => BASIC_TENSES.has(key));
  const advancedGroups = conjGroups.filter(([key]) => ADVANCED_TENSES.has(key) || !BASIC_TENSES.has(key));

  return (
    <div className="space-y-2">
      {conj.length > 0 && (
        <div>
          <h4 className="text-xs font-medium text-zinc-400 mb-2 uppercase tracking-wider">Conjugation ({conj.length})</h4>
          {basicGroups.map(([key, items]) => (
            <TenseGroup key={key} label={key} items={items} groupKey={`conj-${key}`} onSearchForm={onSearchForm} />
          ))}
          {advancedGroups.length > 0 && (
            <>
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="text-xs text-accent-teal hover:text-accent-teal/80 transition-colors mb-2"
              >
                {showAdvanced
                  ? 'Hide advanced tenses'
                  : `Show ${advancedGroups.length} more tenses (${advancedGroups.map(([k]) => k).join(', ')})`}
              </button>
              {showAdvanced && advancedGroups.map(([key, items]) => (
                <TenseGroup key={key} label={key} items={items} groupKey={`conj-${key}`} onSearchForm={onSearchForm} />
              ))}
            </>
          )}
        </div>
      )}
      {decl.length > 0 && (
        <div>
          <h4 className="text-xs font-medium text-zinc-400 mb-2 uppercase tracking-wider">Declension ({decl.length})</h4>
          {groupBy(decl, CASE_ORDER).map(([key, items]) => (
            <TenseGroup key={key} label={key} items={items} groupKey={`decl-${key}`} onSearchForm={onSearchForm} />
          ))}
        </div>
      )}
      {other.length > 0 && (
        <div>
          <h4 className="text-xs font-medium text-zinc-400 mb-2 uppercase tracking-wider">Other forms ({other.length})</h4>
          <div className="flex flex-wrap gap-1.5">
            {other.map((f, i) => (
              <button
                key={i}
                className="px-2 py-0.5 bg-zinc-800 rounded text-xs text-gray-300 hover:bg-zinc-700 hover:text-accent-teal transition-colors cursor-pointer"
                onClick={() => onSearchForm?.(f.form || '')}
              >
                {f.form}
                {f.tags && f.tags.length > 0 && <span className="text-zinc-500 ml-1">({f.tags.slice(0, 3).join(', ')})</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function renderGlossWithLinks(gloss: string, onSearch?: (w: string) => void): string | JSX.Element {
  const pattern = /\b(?:of|from|form of|plural of|feminine of|masculine of|diminutive of|augmentative of|superlative of|comparative of|alternative form of|archaic form of|obsolete form of|dialectal form of)\s+([^\s,;.()]+(?:\s+[^\s,;.()]+)?)/i;
  const m = gloss.match(pattern);
  if (!m || !m[1] || !onSearch) return gloss;
  const lemma = m[1] as string;
  const idx = m.index! + m[0].length - lemma.length;
  const before = gloss.slice(0, idx);
  const after = gloss.slice(idx + lemma.length);
  return (
    <span>
      {before}
      <button className="text-accent-teal hover:underline font-medium" onClick={() => onSearch(lemma)}>{lemma}</button>
      {after}
    </span>
  );
}

function EntryCard({ entry, onSearchForm, onSearchTranslation }: { entry: LexiconEntry; onSearchForm?: (form: string) => void; onSearchTranslation?: (word: string, lang: string) => void }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-bg-secondary border border-zinc-700/50 rounded-lg p-3 md:p-4 mb-3">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h3 className="text-lg md:text-xl font-serif text-gray-100">
          {entry.source === 'glyphs' && entry.unicode_codepoint ? (
            <>
              <span className="text-3xl mr-2" style={{ fontFamily: 'Noto Sans Cuneiform, serif' }}>
                {String.fromCodePoint(parseInt(entry.unicode_codepoint.replace('U+', ''), 16))}
              </span>
              {entry.glyph_name || entry.word}
            </>
          ) : entry.word}
        </h3>
        <PosTag pos={entry.pos} />
        <span className="text-xs text-zinc-500 font-mono">
          {entry.lang_name !== entry.target_language
            ? `${entry.lang_name} (${entry.target_language})`
            : entry.target_language}
        </span>
        <div className="flex items-center gap-2 text-xs text-zinc-500 ml-auto">
          {entry.has_ipa && <span title="Has IPA">IPA</span>}
          {entry.has_audio && <span title="Has audio">Audio</span>}
          {entry.has_forms && (
            <span title="Has inflected forms">{entry.forms_count} forms</span>
          )}
        </div>
      </div>

      {entry.form_of && (
        <div className="mt-2 p-2 bg-zinc-800/60 border border-zinc-700/40 rounded text-sm">
          <span className="text-zinc-400">Inflected form of </span>
          <button
            className="text-accent-teal hover:underline font-medium"
            onClick={() => onSearchForm?.(entry.form_of!.headword || entry.form_of!.lemma_ref.replace(/^wikt#/, '').replace(/#.*$/, ''))}
          >
            {entry.form_of.headword || entry.form_of.lemma_ref.replace(/^wikt#/, '').replace(/#.*$/, '')}
          </button>
          {entry.form_of.form_labels && entry.form_of.form_labels.length > 0 && (
            <span className="text-zinc-500 ml-2">({entry.form_of.form_labels.join(', ')})</span>
          )}
        </div>
      )}

      {entry.parent_entry && (
        <div className="mt-2 p-3 bg-zinc-800/40 border border-zinc-700/30 rounded">
          <div className="flex items-baseline gap-2 mb-1">
            <span className="text-xs text-zinc-500 uppercase tracking-wider">Lemma</span>
            <span className="text-gray-200 font-serif">{entry.parent_entry.word}</span>
            <PosTag pos={entry.parent_entry.pos} />
          </div>
          {entry.parent_entry.transcriptions && Object.keys(entry.parent_entry.transcriptions).length > 0 && (
            <div className="flex flex-wrap gap-2 mb-1">
              {Object.entries(entry.parent_entry.transcriptions).map(([k, v]) => (
                <span key={k} className="text-accent-teal font-mono text-sm">
                  /{v}/
                  <span className="text-zinc-500 text-xs ml-1">{k}</span>
                </span>
              ))}
            </div>
          )}
          {entry.parent_entry.senses && entry.parent_entry.senses.length > 0 && (
            <ol className="space-y-0.5 list-decimal list-inside">
              {entry.parent_entry.senses.slice(0, 5).map((sense, i) => (
                <li key={i} className="text-sm text-gray-300">
                  {(sense.glosses || sense.raw_glosses || []).join('; ')}
                </li>
              ))}
              {entry.parent_entry.senses.length > 5 && (
                <li className="text-xs text-zinc-500">+{entry.parent_entry.senses.length - 5} more</li>
              )}
            </ol>
          )}
          {entry.parent_entry?.definitions && Object.keys(entry.parent_entry.definitions).length > 0 && !entry.parent_entry?.senses?.length && (() => {
            const defs = entry.parent_entry!.definitions!;
            return (
              <div className="text-sm text-gray-300">
                {Object.entries(defs).map(([lang, def]) => (
                  <div key={lang}>
                    <span className="text-zinc-500">{lang}: </span>
                    <button
                      className="text-gray-300 hover:text-accent-teal hover:underline transition-colors text-left"
                      onClick={() => onSearchTranslation?.(((def || '').split(';')[0] ?? '').trim(), lang)}
                      title={`Search in ${lang}`}
                    >{def || ''}</button>
                  </div>
                ))}
              </div>
            );
          })()}
          {entry.parent_entry?.tangut_meta && (
            <div className="flex flex-wrap gap-3 mt-1 text-xs text-zinc-400">
              {Object.entries(entry.parent_entry.tangut_meta).map(([k, v]) => (
                <span key={k}><span className="text-zinc-600">{k.replace(/_/g, ' ')}: </span>{String(v)}</span>
              ))}
            </div>
          )}
        </div>
      )}

      {entry.source === 'glyphs' && (
        <div className="mt-2 space-y-1.5">
          {entry.glyph_name && (
            <div className="text-sm">
              <span className="text-zinc-500">Sign: </span>
              <span className="text-gray-200 font-mono">{entry.glyph_name}</span>
              {entry.unicode_codepoint && (
                <span className="text-zinc-500 text-xs ml-2">{entry.unicode_codepoint}</span>
              )}
            </div>
          )}
          {entry.reading_values && entry.reading_values.length > 0 && (
            <div className="text-sm">
              <span className="text-zinc-500">Readings: </span>
              <span className="text-amber-300/90 font-mono">{entry.reading_values.join(', ')}</span>
            </div>
          )}
          {entry.labat_number && (
            <div className="text-xs text-zinc-500">Labat #{entry.labat_number}</div>
          )}
          {entry.semantic_category && (
            <div className="text-xs text-zinc-500">Category: {entry.semantic_category}</div>
          )}
        </div>
      )}

      <IpaDisplay sounds={entry.sounds} />
      <RomanizationDisplay sounds={entry.sounds} />
      <DialectSynonyms entry={entry} onSearchForm={onSearchForm} />

      {entry.senses.length > 0 && (
        <ol className="mt-3 space-y-1.5 list-decimal list-inside">
          {entry.senses.map((sense, i) => (
            <li key={i} className="text-sm text-gray-300">
              <span>
                {(sense.glosses || sense.raw_glosses || []).map(g => renderGlossWithLinks(g, onSearchForm)).reduce((acc: (string | JSX.Element)[], el, idx) => idx === 0 ? [el] : [...acc, '; ', el], [] as (string | JSX.Element)[])}
              </span>
              {sense.tags && sense.tags.length > 0 && (
                <span className="text-zinc-500 text-xs ml-2">
                  [{sense.tags.join(', ')}]
                </span>
              )}
              {sense.examples?.[0] && (
                <div className="ml-4 mt-1 text-xs text-zinc-400 italic">
                  {sense.examples[0].text}
                  {(sense.examples[0].english ||
                    sense.examples[0].translation) && (
                    <span className="not-italic text-zinc-500">
                      {' '}
                      &mdash;{' '}
                      {sense.examples[0].english ||
                        sense.examples[0].translation}
                    </span>
                  )}
                </div>
              )}
            </li>
          ))}
        </ol>
      )}

      {entry.definitions && Object.keys(entry.definitions).length > 0 && (
        <div className="mt-3">
          <h4 className="text-xs font-medium text-zinc-400 mb-1 uppercase tracking-wider">
            Definitions ({Object.keys(entry.definitions).length} languages)
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-0.5 text-xs">
            {Object.entries(entry.definitions)
              .sort(([a], [b]) => a.localeCompare(b))
              .slice(0, 30)
              .map(([lang, def]) => (
                <div key={lang} className="text-gray-300 leading-relaxed">
                  <span className="text-zinc-500">{lang}: </span>
                  <button
                    className="text-gray-300 hover:text-accent-teal hover:underline transition-colors text-left"
                    onClick={() => onSearchTranslation?.(((def || '').split(';')[0] ?? '').trim(), lang)}
                    title={`Search in ${lang}`}
                  >{(def || '').slice(0, 60)}{(def || '').length > 60 ? '...' : ''}</button>
                </div>
              ))}
            {Object.keys(entry.definitions).length > 30 && (
              <div className="text-zinc-500 text-xs">+{Object.keys(entry.definitions).length - 30} more</div>
            )}
          </div>
        </div>
      )}

      {(() => {
        const allCompounds = entry.compounds || entry.parent_entry?.compounds || null;
        if (!allCompounds || allCompounds.length === 0) return null;
        const trecd = allCompounds.filter((c: Compound) => c.source_dict === 'TRECD');
        const xxwcd = allCompounds.filter((c: Compound) => c.source_dict === 'XXWCD');
        return (
          <div className="mt-3">
            <h4 className="text-xs font-medium text-zinc-400 mb-1 uppercase tracking-wider">
              Compounds ({allCompounds.length})
            </h4>
            {trecd.length > 0 && (
              <div className="mb-2">
                <div className="text-[10px] text-zinc-500 mb-0.5">TRECD</div>
                <div className="space-y-1">
                  {trecd.map((c: Compound, i: number) => {
                    const hc = c.headword_char || '';
                    const n = c.char_count || 2;
                    const slots = Array.from({length: n}, (_, j) => {
                      if (c.headword_position === 'first' && j === 0) return hc;
                      if (c.headword_position === 'non-first' && n === 2 && j === 1) return hc;
                      return null;
                    });
                    const posUnknown = c.headword_position === 'non-first' && n > 2;
                    return (
                      <div key={`t${i}`} className="text-xs text-gray-300 flex items-center gap-2">
                        <span className="text-zinc-500 w-14 shrink-0 font-mono">{c.entry_id}</span>
                        {posUnknown ? (
                          <span className="flex gap-0.5 font-serif text-lg items-center">
                            {Array.from({length: n}, (_, j) => (
                              <span key={j} className="text-zinc-600 text-sm">&#x25A1;</span>
                            ))}
                            <span className="text-[10px] text-zinc-500 ml-1">contains {hc}</span>
                          </span>
                        ) : (
                          <span className="flex gap-0.5 font-serif text-lg">
                            {slots.map((ch, j) => ch
                              ? <span key={j} className="text-gray-100">{ch}</span>
                              : <span key={j} className="text-zinc-600 text-sm">&#x25A1;</span>
                            )}
                          </span>
                        )}
                        <span>{c.gloss_zh}</span>
                        {c.gloss_en && <span className="text-zinc-500">[{c.gloss_en}]</span>}
                        {c.headword_position === 'non-first' && <span className="text-amber-600 text-[10px]">non-initial</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {xxwcd.length > 0 && (
              <div>
                <div className="text-[10px] text-zinc-500 mb-0.5">XXWCD</div>
                <div className="space-y-1">
                  {xxwcd.map((c: Compound, i: number) => {
                    const hc = c.headword_char || '';
                    const prons = c.char_pronunciations || [];
                    const n = c.char_count || 2;
                    const slots = Array.from({length: n}, (_, j) => {
                      const isHead = c.headword_index != null
                        ? j === c.headword_index
                        : (c.headword_position === 'first' && j === 0) ||
                          (c.headword_position === 'non-first' && n === 2 && j === 1);
                      return { char: isHead ? hc : null, pron: prons[j] || null };
                    });
                    return (
                      <div key={`x${i}`} className="text-xs text-gray-300 flex flex-wrap items-center gap-2">
                        {c.volume != null && <span className="text-zinc-500 shrink-0 font-mono">v{c.volume}p{c.page}</span>}
                        <span className="flex gap-0.5 items-end">
                          {slots.map((s, j) => (
                            <span key={j} className="text-center">
                              <span className="block font-serif text-lg leading-none">
                                {s.char ? <span className="text-gray-100">{s.char}</span> : <span className="text-zinc-600 text-sm">&#x25A1;</span>}
                              </span>
                              {s.pron && <span className="block text-[10px] text-blue-400 leading-tight">{s.pron}</span>}
                            </span>
                          ))}
                        </span>
                        <span>{c.gloss_zh}</span>
                        {c.headword_position === 'non-first' && <span className="text-amber-600 text-[10px]">non-initial</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {entry.etymology_text && (
        <p className="mt-3 text-xs text-zinc-500 leading-relaxed">
          {entry.etymology_text.slice(0, 300)}
          {entry.etymology_text.length > 300 && '...'}
        </p>
      )}

      {(entry.forms.length > 0 || entry.translations.length > 0) && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-2 text-xs text-accent-teal hover:text-accent-teal/80 transition-colors"
        >
          {expanded ? 'Show less' : `Show more (${entry.forms_count} forms, ${entry.translations_count} translations)`}
        </button>
      )}

      {expanded && (
        <div className="mt-3 space-y-3">
          {entry.forms.length > 0 && (
            <ParadigmTable forms={entry.forms} onSearchForm={onSearchForm} />
          )}

          {entry.translations.length > 0 && (
            <div>
              <h4 className="text-xs font-medium text-zinc-400 mb-1 uppercase tracking-wider">
                Translations ({entry.translations.length})
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-0.5 text-xs">
                {[...entry.translations]
                  .sort((a, b) => {
                    const oa = FAMILY_ORDER[a.code || ''] ?? 99999;
                    const ob = FAMILY_ORDER[b.code || ''] ?? 99999;
                    if (oa !== ob) return oa - ob;
                    return (a.lang || '').localeCompare(b.lang || '');
                  })
                  .map((t, i) => (
                  <div key={i} className="text-gray-300 leading-relaxed">
                    <span className="text-zinc-500">{t.lang || t.code}: </span>
                    <button
                      className="text-gray-300 hover:text-accent-teal hover:underline transition-colors text-left"
                      onClick={() => onSearchTranslation?.(t.word || '', t.code || '')}
                      title={`Search "${t.word || ''}" in ${t.lang || t.code}`}
                    >{t.word}</button>
                    {t.roman && (
                      <span className="text-amber-300/70 ml-1 font-mono">
                        {t.roman}
                      </span>
                    )}
                    {t.alt && (
                      <span className="text-zinc-400 ml-1">
                        ({t.alt})
                      </span>
                    )}
                    {t.tags && t.tags.length > 0 && (
                      <span className="text-zinc-600 ml-1">
                        [{t.tags.join(', ')}]
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Lexicon() {
  const [query, setQuery] = useState('');
  const [edition, setEdition] = useState('en');
  const [langFilter, setLangFilter] = useState('');
  const [results, setResults] = useState<SearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stripDiacritics, setStripDiacritics] = useState(true);
  const [history, setHistory] = useState<string[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const historyRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setHistory(loadHistory()); }, []);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (historyRef.current && !historyRef.current.contains(e.target as Node) &&
          inputRef.current && !inputRef.current.contains(e.target as Node)) {
        setShowHistory(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const search = useCallback(
    async (word?: string) => {
      const searchWord = word ?? query;
      if (!searchWord.trim()) return;
      if (word) setQuery(word);
      setLoading(true);
      setError(null);
      setShowHistory(false);
      setHistory(addToHistory(searchWord));
      try {
        const params = new URLSearchParams({
          word: searchWord.trim(),
          edition,
          limit: '100',
        });
        if (langFilter) params.set('lang', langFilter);
        if (stripDiacritics) params.set('normalize', '1');
        const res = await fetch(
          `${API_BASE}/api/admin/lexicon/search?${params}`,
        );
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        const data: SearchResponse = await res.json();
        setResults(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Search failed');
      } finally {
        setLoading(false);
      }
    },
    [query, edition, langFilter, stripDiacritics],
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') search();
  };

  const groupedByLang = results
    ? Object.entries(
        results.items.reduce(
          (acc, item) => {
            const lang = item.target_language;
            if (!acc[lang]) acc[lang] = [];
            acc[lang].push(item);
            return acc;
          },
          {} as Record<string, LexiconEntry[]>,
        ),
      ).sort(([, a], [, b]) => b.length - a.length)
    : [];

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-xl md:text-2xl font-serif text-gray-100 mb-4 md:mb-6">Lexicon</h1>

      <div className="space-y-3 mb-6">
        <div className="flex gap-2">
          <div className="flex-1 min-w-0 relative">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => { if (history.length > 0) setShowHistory(true); }}
              placeholder="Search for a word..."
              className="w-full px-3 md:px-4 py-2.5 md:py-3 bg-bg-secondary border border-zinc-700 rounded-lg text-gray-100 text-base md:text-lg placeholder-zinc-500 focus:outline-none focus:border-accent-teal/50 focus:ring-1 focus:ring-accent-teal/30"
              autoFocus
            />
            {showHistory && history.length > 0 && (
              <div ref={historyRef} className="absolute z-50 left-0 right-0 top-full mt-1 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl max-h-72 overflow-y-auto">
                <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-700/50">
                  <span className="text-xs text-zinc-500">Recent searches ({history.length})</span>
                  <button
                    onClick={(e) => { e.stopPropagation(); saveHistory([]); setHistory([]); setShowHistory(false); }}
                    className="text-xs text-zinc-600 hover:text-zinc-400 transition-colors"
                  >
                    Clear all
                  </button>
                </div>
                {history.map((term, i) => (
                  <button
                    key={`${term}-${i}`}
                    className="w-full text-left px-3 py-2 text-sm text-zinc-300 hover:bg-zinc-700 hover:text-gray-100 transition-colors border-b border-zinc-800/50 last:border-0"
                    onClick={() => { setQuery(term); setShowHistory(false); search(term); }}
                  >
                    {term}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={() => search()}
            disabled={loading || !query.trim()}
            className="px-4 md:px-6 py-2 bg-accent-teal/20 text-accent-teal rounded-lg hover:bg-accent-teal/30 disabled:opacity-40 disabled:cursor-not-allowed transition-colors font-medium shrink-0"
          >
            {loading ? '...' : 'Search'}
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          <select
            value={edition}
            onChange={(e) => setEdition(e.target.value)}
            className="w-full sm:w-auto px-3 py-2 bg-bg-secondary border border-zinc-700 rounded-lg text-gray-300 text-sm focus:outline-none focus:border-accent-teal/50"
          >
            <optgroup label="Imported">
              <option value="en">en — English Wiktionary</option>
              <option value="fr">fr — French Wiktionary</option>
              <option value="de">de — German Wiktionary</option>
              <option value="ru">ru — Russian Wiktionary</option>
              <option value="zh">zh — Chinese Wiktionary</option>
              <option value="es">es — Spanish Wiktionary</option>
              <option value="el">el — Greek Wiktionary</option>
              <option value="it">it — Italian Wiktionary</option>
              <option value="ja">ja — Japanese Wiktionary</option>
              <option value="ko">ko — Korean Wiktionary</option>
              <option value="ku">ku — Kurdish Wiktionary</option>
              <option value="nl">nl — Dutch Wiktionary</option>
              <option value="pl">pl — Polish Wiktionary</option>
              <option value="pt">pt — Portuguese Wiktionary</option>
              <option value="cs">cs — Czech Wiktionary</option>
              <option value="id">id — Indonesian Wiktionary</option>
              <option value="ms">ms — Malay Wiktionary</option>
            </optgroup>
            <optgroup label="Special Scripts">
              <option value="cuneiform">Cuneiform Signs</option>
            </optgroup>
          </select>
          <input
            type="text"
            value={langFilter}
            onChange={(e) => setLangFilter(e.target.value.toLowerCase())}
            placeholder="lang filter (e.g. fra)"
            className="w-full sm:w-40 px-3 py-2 bg-bg-secondary border border-zinc-700 rounded-lg text-gray-300 text-sm placeholder-zinc-500 focus:outline-none focus:border-accent-teal/50"
            title="Filter by 3-letter language code (e.g., fra, jpn)"
          />
          <label
            className="flex items-center gap-1.5 px-3 py-2 text-sm cursor-pointer select-none"
            title="Strip Arabic/Hebrew vowel marks, Russian stress marks, and other diacritics before searching"
          >
            <input
              type="checkbox"
              checked={stripDiacritics}
              onChange={(e) => setStripDiacritics(e.target.checked)}
              className="accent-accent-teal"
            />
            <span className="text-zinc-400">Strip diacritics</span>
          </label>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-900/30 border border-red-700/50 rounded-lg text-red-300 text-sm">
          {error}
        </div>
      )}

      {results && (
        <div>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <p className="text-sm text-zinc-400">
              {results.count} entries for{' '}
              <span className="text-gray-200 font-medium">
                &ldquo;{results.word}&rdquo;
              </span>{' '}
              across {groupedByLang.length} language
              {groupedByLang.length !== 1 && 's'}
            </p>
            <button
              onClick={() => { setResults(null); setQuery(''); setTimeout(() => inputRef.current?.focus(), 50); }}
              className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
            >
              Clear results
            </button>
          </div>

          {results.count === 0 && (
            <p className="text-zinc-500 text-center py-12">
              No entries found. Try a different word or edition.
            </p>
          )}

          {groupedByLang.map(([lang, entries]) => (
            <div key={lang} className="mb-6">
              {groupedByLang.length > 1 && (
                <h2 className="text-sm font-medium text-zinc-400 tracking-wider mb-2 border-b border-zinc-800 pb-1">
                  {entries[0]?.lang_name !== lang
                    ? `${entries[0]?.lang_name} (${lang})`
                    : lang}{' '}
                  <span className="text-zinc-600">— {entries.length} entries</span>
                </h2>
              )}
              {entries.map((entry, i) => (
                <EntryCard key={`${lang}-${entry.pos}-${i}`} entry={entry} onSearchForm={(form) => { setQuery(form); search(form); }} onSearchTranslation={(word, tLang) => { setQuery(word); if (tLang) setLangFilter(tLang); search(word); }} />
              ))}
            </div>
          ))}
        </div>
      )}

      {!results && !loading && (
        <div className="py-8">
          {history.length > 0 ? (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-medium text-zinc-400">Recent searches</h2>
                <button
                  onClick={() => { saveHistory([]); setHistory([]); }}
                  className="text-xs text-zinc-600 hover:text-zinc-400 transition-colors"
                >
                  Clear history
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {history.map((term, i) => (
                  <button
                    key={`${term}-${i}`}
                    onClick={() => { setQuery(term); search(term); }}
                    className="px-3 py-1.5 bg-zinc-800 border border-zinc-700/50 rounded-lg text-sm text-zinc-300 hover:bg-zinc-700 hover:text-gray-100 hover:border-zinc-600 transition-colors"
                  >
                    {term}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-zinc-500 text-lg mb-2">
                Search the Wiktionary lexical database
              </p>
              <p className="text-zinc-600 text-sm">
                10.6M entries from English Wiktionary &middot; IPA, definitions,
                forms, translations
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
